import { useCallback, useEffect, useState } from 'react';
import type { SelectChoice } from '../components/ui/Input';
import { apiGetFresh } from './api-client';

export interface ListedRecord {
  id: string;
  code?: string;
  name?: string;
  description?: string;
  customerCode?: string;
  customerName?: string;
  customerId?: string;
  poNumber?: string;
  billingEmails?: string[];
  contactName?: string;
  postalAddress?: string;
  kind?: string;
  engagementId?: string;
  lines?: { id?: string; reference?: string; description?: string; amountPence?: number; remainingPence?: number }[];
}

function text(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

export function customerChoices(rows: ListedRecord[], blank = 'Select'): SelectChoice[] {
  return [
    { value: '', label: blank },
    ...rows.map((row) => ({ value: row.id, code: row.code || '', name: row.name || '', description: row.description || '' })),
  ];
}

export function engagementChoices(rows: ListedRecord[], blank = 'Select'): SelectChoice[] {
  return [
    { value: '', label: blank },
    ...rows.map((row) => ({ value: row.id, code: row.customerCode || '', name: row.customerName || '', description: row.name || '' })),
  ];
}

export function purchaseOrderChoices(rows: ListedRecord[], blank = 'PO'): SelectChoice[] {
  return [
    { value: '', label: blank },
    ...rows.map((row) => ({
      value: row.id,
      code: row.poNumber || '',
      name: [row.customerCode, row.customerName].filter(Boolean).join(' '),
      description: (row.lines || []).map((line) => text(line.description)).filter(Boolean).join('; '),
    })),
  ];
}

export function lookupChoice(url: string, item: Record<string, unknown>, labelKey: string): SelectChoice & { label: string; customerName?: string; engagementName?: string } {
  const value = String(item.id ?? item.code ?? '');
  if (url.startsWith('/api/customers')) {
    const code = text(item.code);
    const name = text(item.name);
    const description = text(item.description);
    return { value, code, name, description, label: [code, name, description].filter(Boolean).join(' · ') || value, customerName: name };
  }
  if (url.startsWith('/api/engagements')) {
    const code = text(item.customerCode);
    const name = text(item.customerName);
    const description = text(item.name);
    return { value, code, name, description, label: [code, name, description].filter(Boolean).join(' · ') || value, customerName: name, engagementName: description };
  }
  if (url.startsWith('/api/purchase-orders')) {
    const code = text(item.poNumber);
    const name = [text(item.customerCode), text(item.customerName)].filter(Boolean).join(' ');
    const description = Array.isArray(item.lines)
      ? item.lines.map((line) => (line && typeof line === 'object' ? text((line as Record<string, unknown>).description) : '')).filter(Boolean).join('; ')
      : '';
    return { value, code, name, description, label: [code, name, description].filter(Boolean).join(' · ') || value, customerName: text(item.customerName) };
  }
  const name = String(item[labelKey] || item.name || item.id);
  const customerName = item.customerName ? String(item.customerName) : undefined;
  return { value, label: customerName ? `${customerName} · ${name}` : name, customerName };
}

export function useFreshRows<T>(url: string, enabled = true) {
  const [rows, setRows] = useState<T[]>([]);
  const reload = useCallback(async () => {
    if (!enabled) return;
    setRows(await apiGetFresh<T[]>(url));
  }, [enabled, url]);
  useEffect(() => {
    void reload().catch(() => undefined);
  }, [reload]);
  return [rows, reload] as const;
}
